import { isGlMapHideOperation } from '@festina/configuration-general-types/configuration'
import { dateUtils } from '@festina-life/core'
import { Alert, FullscreenSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack, Typography } from '@festina-life/ui/structure'
import { Group, Panel, useDefaultLayout } from 'react-resizable-panels'

import { OperationValidationErrors } from '../../components/operationErrors/OperationValidationErrors'
import { useConfigurationContext } from '../../context/configurationContext'
import { useMappingId } from '../../system/useMappingId'
import { useNavigationParams } from '../../system/useNavigationParams'
import {
  getGlMapOperationAtDate,
  getGlMapOperationFromDate,
} from '../../uiModels/glMapOperations/helpers'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { GlMapSidebarPanel } from './GlMapSidebarPanel'
import { GlMapViewerTopBar } from './GlMapViewerTopBar'

/**
 * Viewer for a single GL-map version. GL-map entries are only editable through Excel,
 * so the viewer intentionally shows the metadata of the version in scope and never
 * the entries themselves.
 */
export const GlMapViewer = () => {
  const translate = useTranslate()
  const mappingId = useMappingId()
  const { block } = useNavigationParams()
  const { viewAt } = useConfigurationContext()
  const { isLoading } = useOperationsContext()
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'glMapViewerPanel',
    storage: localStorage,
  })
  const { applicationGlMaps } = useApplicationViewOperationsContext()
  const indexedGlMapOperations = applicationGlMaps.get(mappingId)
  const glMapOperation = getGlMapOperationAtDate(indexedGlMapOperations, viewAt)
  const operation = glMapOperation?.internalOperation

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  return (
    <Group
      orientation="horizontal"
      defaultLayout={defaultLayout}
      onLayoutChanged={onLayoutChanged}
      className="size-full"
    >
      <GlMapSidebarPanel />
      <Panel className="size-full overflow-hidden" id="glMapCentralView">
        {operation ? (
          <div className="grid size-full grid-rows-[auto_1fr] overflow-hidden">
            <GlMapViewerTopBar glMapOperation={glMapOperation} />
            <div className="size-full overflow-y-auto">
              <Stack
                className="flex-col max-w-xl"
                gap="spacious"
                padding="spacious"
              >
                <OperationValidationErrors
                  operationKey={operation.operationKey}
                />
                <Stack className="flex-col">
                  <Typography size="lg">
                    {translate('glMapViewer.from.label')}
                  </Typography>
                  <Typography>
                    {dateUtils.dateAsStr(getGlMapOperationFromDate(operation))}
                  </Typography>
                </Stack>
                {isGlMapHideOperation(operation) ? (
                  <Alert
                    severity="info"
                    title={translate('glMapViewer.hiddenGlMap', { mappingId })}
                  />
                ) : (
                  <Stack className="flex-col">
                    <Typography size="lg">
                      {translate('glMapViewer.notes.label')}
                    </Typography>
                    <Typography>
                      {operation.glMap?.notes?.trim() ||
                        translate('glMapViewer.notes.emptyState')}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </div>
          </div>
        ) : (
          <Stack className="size-full items-center justify-center">
            <Alert
              severity="error"
              title={translate('glMapViewer.noGlMap', {
                mappingId,
                block,
                viewAt: dateUtils.dateAsStr(viewAt),
              })}
            />
          </Stack>
        )}
      </Panel>
    </Group>
  )
}
